// Match States — types + shaping (Phase 3, D3 SVG radial chart).
//
// A radial "clock": six 15-min time bands as sectors, each drawn as N concentric
// rings (one per match in the window) coloured by the team's W/D/L state during
// that band. The radial geometry lives in the component (a full clear+redraw, no
// animation); the pure part is picking the window + turning the W/D/L fractions
// into per-sector ring counts.

/** Venue selector. */
export type MsHa = "All" | "Home" | "Away";
/** Form window. */
export type MsWindow = "5" | "10" | "Season";

/** One time band's win/draw/loss fractions (each 0–1). */
export interface MsBand {
  w: number;
  d: number;
  l: number;
}

/** Match-states data: team → venue → window → band label → fractions. */
export type MatchStatesData = Record<
  string,
  Record<MsHa, Record<MsWindow, Record<string, MsBand>>>
>;

/** The six time bands, inner→outer sector order. */
export const MS_BANDS = [
  "0-15",
  "15-30",
  "30-45",
  "45-60",
  "60-75",
  "75-90",
] as const;

/** One sector: its band + how many of the N rings are losses/draws/wins.
 *  Rings fill centre→edge in the order loss (red) → draw (grey) → win (green). */
export interface MsSector {
  band: string;
  lCount: number;
  dCount: number;
  wCount: number;
}

/** Shaped match-states widget: rings per sector + the six sectors. */
export interface MatchStatesShape {
  /** Rings per sector = matches in the window. */
  n: number;
  sectors: MsSector[];
}

function toHa(ha: string | null | undefined): MsHa {
  return ha === "Home" ? "Home" : ha === "Away" ? "Away" : "All";
}
function toWindow(timeline: string | null | undefined): MsWindow {
  return timeline === "10" ? "10" : timeline === "Season" ? "Season" : "5";
}

/**
 * Rings per sector = the number of matches in the window. A venue filter (Home
 * or Away) roughly halves the sample, matching the legacy renderer:
 *   Season → 38 (All) / 19 (H or A); Last 10 → 10 / 5; Last 5 → 5.
 */
function ringsPerSector(ha: MsHa, w: MsWindow): number {
  if (w === "Season") return ha !== "All" ? 19 : 38;
  if (w === "10") return ha !== "All" ? 5 : 10;
  return 5;
}

/**
 * Resolve the sectors for a team + venue + window, or null when there's no data.
 * Each band's fractions are scaled to N rings: losses and wins round to the
 * nearest ring, draws take the remainder (so the three always sum to exactly N),
 * matching the legacy `Math.round(l*N)` / `Math.round(w*N)` / `N - l - w`.
 */
export function shapeMatchStates(
  data: MatchStatesData,
  team: string,
  ha: string | null | undefined,
  timeline: string | null | undefined
): MatchStatesShape | null {
  const h = toHa(ha);
  const w = toWindow(timeline);
  const td = data[team]?.[h]?.[w];
  if (!td) return null;
  const n = ringsPerSector(h, w);
  const sectors: MsSector[] = MS_BANDS.map((band) => {
    const bd = td[band] ?? { w: 0, d: 0, l: 0 };
    const lCount = Math.round(bd.l * n);
    const wCount = Math.round(bd.w * n);
    const dCount = n - lCount - wCount;
    return { band, lCount, dCount, wCount };
  });
  return { n, sectors };
}
